/**
 * LabelDashboardDeployConfirmDialog
 * Confirmation dialog for deploying a single label to multiple tenants (dashboard view).
 * Shows each selected tenant with the label's current status for that tenant.
 */

import React from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import type { LabelEntity } from '../../services/types';
import { StatusBadge, StatusType } from '../StatusBadge';
import { DIALOG_STYLES, CONTAINER_STYLES, SECTION_HEADER_STYLES, COLORS, TYPOGRAPHY } from '../policies/constants';

function getTenantStatus(label: LabelEntity, tenantId: string): StatusType {
  const td = label.tenantDeployments.find(t => t.tenantId.toLowerCase() === tenantId.toLowerCase());
  return td?.deploymentStatus === 'Deployed' ? 'Available' : 'Not Available';
}

type Props = {
  open: boolean;
  label: LabelEntity | null;
  tenantIds: string[];
  tenantNamesById: Map<string, string>;
  tenantPrefixById?: Map<string, string>;
  baseTenantName?: string;
  deploying?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export default function LabelDashboardDeployConfirmDialog({
  open,
  label,
  tenantIds,
  tenantNamesById,
  tenantPrefixById,
  baseTenantName,
  deploying = false,
  onConfirm,
  onClose,
}: Props) {
  const base = baseTenantName || 'Base Tenant';
  if (!label) return null;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={DIALOG_STYLES.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={onClose}
              />
            }
          >
            Deploy Labels – {base} → {tenantIds.length} tenant{tenantIds.length !== 1 ? 's' : ''}
          </DialogTitle>

          <DialogContent style={{ padding: 0 }}>
            <div style={CONTAINER_STYLES.infoMessage}>
              <Text style={{ ...TYPOGRAPHY.body, color: COLORS.gray600 } as React.CSSProperties}>
                <strong>{label.data.displayName}</strong> will be deployed to the following{' '}
                <strong>
                  {tenantIds.length} tenant{tenantIds.length !== 1 ? 's' : ''}
                </strong>:
              </Text>
              <Text
                style={
                  {
                    ...TYPOGRAPHY.body,
                    color: COLORS.gray600,
                    display: 'block',
                    marginTop: 8,
                  } as React.CSSProperties
                }
              >
                When first created, parent and standalone labels are prefixed with
                each tenant's configured prefix; existing labels are never renamed, and child labels
                are not prefixed.
              </Text>
            </div>

            <div
              style={{
                border: '1px solid #e5e5e5',
                borderRadius: 6,
                overflow: 'hidden',
                maxHeight: 320,
                overflowY: 'auto',
                margin: '16px 24px',
              }}
            >
              {/* Header row */}
              <div
                style={{
                  ...SECTION_HEADER_STYLES.sticky,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(160px,1.5fr) minmax(90px,0.7fr) auto',
                }}
              >
                <div>Tenant</div>
                <div>Prefix</div>
                <div>Current status</div>
              </div>

              {/* Tenant rows */}
              {tenantIds.map((tenantId, i) => {
                const tenantName = tenantNamesById.get(tenantId.toLowerCase()) ?? tenantId;
                const prefix = tenantPrefixById?.get(tenantId.toLowerCase()) ?? '';
                const status = getTenantStatus(label, tenantId);
                return (
                  <div
                    key={tenantId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(160px,1.5fr) minmax(90px,0.7fr) auto',
                      padding: '10px 16px',
                      borderBottom: i === tenantIds.length - 1 ? 'none' : '1px solid #f0f0f0',
                      alignItems: 'center',
                      fontSize: 14,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{tenantName}</div>
                      <div style={{ fontSize: 12, color: COLORS.gray600 }}>{tenantId}</div>
                    </div>
                    <div>
                      {prefix ? (
                        <span style={{ fontWeight: 600, color: '#0078d4' }}>{prefix}</span>
                      ) : (
                        <span style={{ color: COLORS.gray600 }}>—</span>
                      )}
                    </div>
                    <div>
                      <StatusBadge status={status} />
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={deploying}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onConfirm} disabled={deploying}>
              {deploying ? 'Deploying…' : 'Deploy'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
